import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supplierAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Save, Lock, Building2, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function SupplierProfile() {
    const { user, updateUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({});
    const [newPassword, setNewPassword] = useState('');

    const loadProfile = useCallback(async () => {
        try {
            const res = await supplierAPI.getProfile();
            setProfile(res.data);
            setFormData({
                contact_name: res.data.contact_name || '',
                contact_function: res.data.contact_function || '',
                phone_fixed: res.data.phone_fixed || '',
                phone_mobile: res.data.phone_mobile || '',
                address: res.data.address || '',
            });
        } catch (err) {
            toast.error('Erreur chargement profil');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadProfile(); }, [loadProfile]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { ...formData };
            if (newPassword) payload.password = newPassword;
            const res = await supplierAPI.updateProfile(payload);
            toast.success('Profil mis à jour');
            setProfile(res.data.supplier);
            if (res.data.supplier.contact_name) {
                updateUser({ contact_name: res.data.supplier.contact_name });
            }
            setNewPassword('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur mise à jour');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">Chargement...</div></div>;

    return (
        <div className="space-y-6 max-w-2xl" data-testid="supplier-profile">
            <h1 className="text-xl font-bold">Mon Profil</h1>

            {/* Read-only info */}
            <Card className="rounded-xl">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-emerald-600" />
                        Informations société (non modifiables)
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-gray-500">Code fournisseur</Label>
                            <p className="font-mono font-bold text-emerald-700 text-lg">{profile?.code}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Société</Label>
                            <p className="font-medium">{profile?.company_name}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">SIRET</Label>
                            <p className="text-sm">{profile?.siret || '-'}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">N° TVA</Label>
                            <p className="text-sm">{profile?.tva_number || '-'}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Email</Label>
                            <p className="text-sm">{profile?.email}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Familles produit</Label>
                            <p className="text-sm">{profile?.product_families?.join(', ') || '-'}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Editable info */}
            <Card className="rounded-xl">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <User className="w-4 h-4 text-emerald-600" />
                        Informations contact
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs">Nom du contact</Label>
                            <Input value={formData.contact_name} onChange={e => setFormData(d => ({ ...d, contact_name: e.target.value }))} />
                        </div>
                        <div>
                            <Label className="text-xs">Fonction</Label>
                            <Input value={formData.contact_function} onChange={e => setFormData(d => ({ ...d, contact_function: e.target.value }))} />
                        </div>
                        <div>
                            <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" />Tél. fixe</Label>
                            <Input value={formData.phone_fixed} onChange={e => setFormData(d => ({ ...d, phone_fixed: e.target.value }))} />
                        </div>
                        <div>
                            <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" />Tél. portable</Label>
                            <Input value={formData.phone_mobile} onChange={e => setFormData(d => ({ ...d, phone_mobile: e.target.value }))} />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />Adresse</Label>
                        <Input value={formData.address} onChange={e => setFormData(d => ({ ...d, address: e.target.value }))} />
                    </div>
                </CardContent>
            </Card>

            {/* Password */}
            <Card className="rounded-xl">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Lock className="w-4 h-4 text-emerald-600" />
                        Changer le mot de passe
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Input type="password" placeholder="Nouveau mot de passe (laisser vide pour ne pas changer)" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                </CardContent>
            </Card>

            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
        </div>
    );
}
